import { useTranslation } from 'react-i18next';

interface DetailRowProps {
  label: string;
  value: string;
}

function DetailRow({ label, value }: DetailRowProps) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-4 py-3 border-b border-gray-200 last:border-b-0">
      <dt className="text-sm font-semibold text-slate-primary sm:w-64 shrink-0">
        {label}
      </dt>
      <dd className="text-sm text-gray-700">
        {value}
      </dd>
    </div>
  );
}

export default function ContentConfidentialite() {
  const { t } = useTranslation('confidentialite');

  return (
    <section className="py-10 md:py-14">
      <div className="max-w-3xl mx-auto px-4 md:px-6">
        <div className="space-y-12">
          {/* Intro */}
          <div className="bg-soft-gray rounded-lg p-5 md:p-6">
            <p className="text-sm md:text-base text-gray-700 leading-relaxed">
              {t('intro_text')}
            </p>
          </div>

          {/* Responsable */}
          <div>
            <h2 className="text-xl md:text-2xl font-manrope font-bold text-slate-primary mb-6">
              {t('responsable_h2')}
            </h2>
            <dl className="bg-soft-gray rounded-lg p-5 md:p-6">
              <DetailRow label={t('responsable_raison')} value={t('responsable_raison_val')} />
              <DetailRow label={t('responsable_adresse')} value={t('responsable_adresse_val')} />
              <DetailRow label={t('responsable_prp')} value={t('responsable_prp_val')} />
              <DetailRow label={t('responsable_email')} value={t('responsable_email_val')} />
              <DetailRow label={t('responsable_tel')} value={t('responsable_tel_val')} />
            </dl>
          </div>

          {/* Données collectées */}
          <div>
            <h2 className="text-xl md:text-2xl font-manrope font-bold text-slate-primary mb-5">
              {t('donnees_h2')}
            </h2>
            <ul className="space-y-3">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <li key={i} className="flex items-start gap-3 text-sm md:text-base text-gray-700">
                  <span className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
                    <i className="ri-check-line text-copper" />
                  </span>
                  {t(`donnees_${i}`)}
                </li>
              ))}
            </ul>
          </div>

          {/* Finalités */}
          <div>
            <h2 className="text-xl md:text-2xl font-manrope font-bold text-slate-primary mb-5">
              {t('finalites_h2')}
            </h2>
            <ul className="space-y-3">
              {[1, 2, 3, 4, 5].map((i) => (
                <li key={i} className="flex items-start gap-3 text-sm md:text-base text-gray-700">
                  <span className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
                    <i className="ri-check-line text-copper" />
                  </span>
                  {t(`finalites_${i}`)}
                </li>
              ))}
            </ul>
          </div>

          {/* Conservation */}
          <div>
            <h2 className="text-xl md:text-2xl font-manrope font-bold text-slate-primary mb-4">
              {t('conservation_h2')}
            </h2>
            <p className="text-sm md:text-base text-gray-700 leading-relaxed">
              {t('conservation_text')}
            </p>
          </div>

          {/* Partage */}
          <div>
            <h2 className="text-xl md:text-2xl font-manrope font-bold text-slate-primary mb-4">
              {t('partage_h2')}
            </h2>
            <p className="text-sm md:text-base text-gray-700 leading-relaxed">
              {t('partage_text')}
            </p>
          </div>

          {/* Droits Loi 25 */}
          <div>
            <h2 className="text-xl md:text-2xl font-manrope font-bold text-slate-primary mb-5">
              {t('droits_h2')}
            </h2>
            <ul className="space-y-3 mb-5">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <li key={i} className="flex items-start gap-3 text-sm md:text-base text-gray-700">
                  <span className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
                    <i className="ri-shield-check-line text-copper" aria-hidden="true" />
                  </span>
                  {t(`droits_${i}`)}
                </li>
              ))}
            </ul>
            <div className="bg-soft-gray rounded-lg p-4 md:p-5">
              <p className="text-sm font-semibold text-slate-primary mb-1">
                {t('droits_exercer')}
              </p>
              <p className="text-sm text-gray-700">
                Envoyez un courriel à{' '}
                <a href="mailto:contact@couvreur-levis.com" className="text-copper hover:underline">contact@couvreur-levis.com</a>
                {' '}ou appelez au{' '}
                <a href="tel:+14184769950" aria-label="Appeler Couvreur-Lévis.com au 418-476-9950" title="Cliquez pour appeler" className="text-copper hover:underline">418-476-9950</a>.
                Nous répondrons dans un délai de 30 jours selon les exigences de la Loi 25.
              </p>
            </div>
          </div>

          {/* Cookies */}
          <div>
            <h2 className="text-xl md:text-2xl font-manrope font-bold text-slate-primary mb-4">
              {t('cookies_h2')}
            </h2>
            <p className="text-sm md:text-base text-gray-700 leading-relaxed">
              {t('cookies_text')}
            </p>
          </div>

          {/* Sécurité */}
          <div>
            <h2 className="text-xl md:text-2xl font-manrope font-bold text-slate-primary mb-4">
              {t('securite_h2')}
            </h2>
            <p className="text-sm md:text-base text-gray-700 leading-relaxed">
              {t('securite_text')}
            </p>
          </div>

          {/* Modifications */}
          <div>
            <h2 className="text-xl md:text-2xl font-manrope font-bold text-slate-primary mb-4">
              {t('modifications_h2')}
            </h2>
            <p className="text-sm md:text-base text-gray-700 leading-relaxed">
              {t('modifications_text')}
            </p>
          </div>

          {/* Contact */}
          <div>
            <h2 className="text-xl md:text-2xl font-manrope font-bold text-slate-primary mb-4">
              {t('contact_h2')}
            </h2>
            <p className="text-sm md:text-base text-gray-700 leading-relaxed">
              Pour toute question concernant cette politique de confidentialité ou pour exercer vos droits, contactez-nous à{' '}
              <a href="mailto:contact@couvreur-levis.com" className="text-copper hover:underline">contact@couvreur-levis.com</a>
              {' '}ou au{' '}
              <a href="tel:+14184769950" aria-label="Appeler Couvreur-Lévis.com au 418-476-9950" title="Cliquez pour appeler" className="text-copper hover:underline">418-476-9950</a>.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}